/**
 * Card for a single role binding rule.
 */
import React from 'react';
import { GripVertical, Trash2 } from 'lucide-react';
import { EntityFilterSelector } from './EntityFilterSelector';
import { ReadScopeBuilder } from './ReadScopeBuilder';
import { ActionsChecklist } from './ActionsChecklist';
import { clampRuleWriteScopes, ruleCardTitle } from '@/components/roles/role-form-utils';
import type { RoleBindingRule, RuleEntityFilter, RuleReadScope } from '@/types/role.types';
import type { InterfaceElement } from '@/types/interface-elements.types';

/** Props for {@link RuleCard}. */
interface RuleCardProps {
  rule: RoleBindingRule;
  interfaceName: string;
  availableActions: InterfaceElement[];
  onUpdate: (updatedRule: RoleBindingRule) => void;
  onDelete: () => void;
  disabled?: boolean;
  isDefault?: boolean;
  entityFilterLocked?: boolean;
  cardLabel?: string;
}

/** Card for a single role binding rule. */
export function RuleCard({
  rule,
  interfaceName,
  availableActions,
  onUpdate,
  onDelete,
  disabled,
  isDefault,
  entityFilterLocked,
  cardLabel,
}: Readonly<RuleCardProps>) {
  const handleEntityFilterChange = (filter: RuleEntityFilter | null) => {
    onUpdate({ ...rule, entityFilter: filter });
  };

  const handleReadScopeChange = (scope: RuleReadScope | null) => {
    onUpdate({ ...rule, read: scope });
  };

  const handleActionsChange = (actionIds: string[]) => {
    onUpdate({ ...rule, actions: actionIds });
  };

  const handleWriteScopesChange = (writeScopes: Record<string, RuleReadScope | null>) => {
    onUpdate({ ...rule, writeScopes });
  };

  // Keep a ref to the latest rule + onUpdate so the effect below does not capture an old closure
  const ruleRef = React.useRef(rule);
  const onUpdateRef = React.useRef(onUpdate);
  React.useEffect(() => {
    ruleRef.current = rule;
    onUpdateRef.current = onUpdate;
  });

  // Auto-clamp write scopes when visibility scope changes
  React.useEffect(() => {
    const currentRule = ruleRef.current;
    const clampedWriteScopes = clampRuleWriteScopes(
      currentRule.writeScopes,
      currentRule.read ?? null,
    );
    if (clampedWriteScopes) {
      onUpdateRef.current({ ...currentRule, writeScopes: clampedWriteScopes });
    }
  }, [rule.read]); // Only fires when read scope changes

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
          <span className="text-sm font-medium text-gray-900">
            {ruleCardTitle(isDefault, cardLabel, rule.entityFilter?.field)}
          </span>
        </div>
        {!isDefault && !disabled && !entityFilterLocked && (
          <button
            type="button"
            onClick={onDelete}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
            title="Supprimer cette règle"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        )}
      </div>

      <div className="p-4 space-y-4">
        {!isDefault && (
          <EntityFilterSelector
            interfaceName={interfaceName}
            value={rule.entityFilter}
            onChange={handleEntityFilterChange}
            disabled={disabled || entityFilterLocked}
          />
        )}

        <ReadScopeBuilder
          value={rule.read}
          onChange={handleReadScopeChange}
          disabled={disabled}
          previewPrefix="Voit seulement"
        />

        <ActionsChecklist
          actions={availableActions}
          selectedActionIds={rule.actions}
          onChange={handleActionsChange}
          writeScopes={rule.writeScopes}
          onWriteScopesChange={handleWriteScopesChange}
          disabled={disabled}
          visibilityScope={rule.read ?? null}
        />
      </div>
    </div>
  );
}
